'use client'

import { format } from 'date-fns'
import { WarningCircleIcon } from '@phosphor-icons/react'
import {
  DateField,
  MultiSelectProperty,
  PROPERTY_ICONS,
  InlinePropertyPopover,
} from '@/ui/property-fields'
import { ICON_SIZES, FOCUS_STYLES } from '@/ui/design-tokens'
import { cn } from '@/lib/tailwind'
import { TopicSubtitleSelector } from './TopicSubtitleSelector'
import { SuggestedPeopleChips } from '../form-sections'

interface MetadataRowProps {
  /** Current entry type */
  effectiveType: 'task' | 'journal' | null

  /** Due date (task mode only) */
  dueDate: Date | null
  onDueDateChange: (date: Date | null) => void

  /** Importance toggle (task mode only) */
  important: boolean
  onImportantChange: (important: boolean) => void

  /** Topics */
  selectedTopicIds: string[]
  availableTopics: Array<{ id: string; name: string }>
  onTopicsChange: (topicIds: string[]) => void
  onAddTopic: () => void
  onCreateTopic?: (name: string) => void

  /** People */
  selectedPeople: Array<{ id: string; title: string }>
  availablePeople: Array<{ id: string; title: string }>
  onAddPerson: (person: { id: string; title: string }) => void
  onRemovePerson: (person: { id: string; title: string }) => void
  isPeopleLoading: boolean
  onAddNewPerson: () => void

  /** Suggested people from AI */
  suggestedPeople: string[]
  addingPerson: string | null
  onAddSuggestedPerson: (name: string) => void
  onDismissSuggestedPerson: (name: string) => void

  /** Project selector (only shown if projects exist) */
  selectedProject: { id: string; name: string } | null
  projectOptions: Array<{ value: string; label: string }>
  onProjectChange: (value: string | null) => void

  /** When true, project is shown as a static badge and cannot be changed */
  projectLocked?: boolean

  /** Disabled state */
  disabled: boolean
}

/**
 * ImportanceToggle - compact pill button for marking tasks as important.
 * Active: red bg with filled WarningCircleIcon icon.
 * Inactive: dashed border with regular WarningCircleIcon icon.
 */
function ImportanceToggle({
  important,
  onChange,
  disabled,
}: {
  important: boolean
  onChange: (value: boolean) => void
  disabled: boolean
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!important)}
      disabled={disabled}
      aria-label={important ? 'Remove importance' : 'Mark as important'}
      aria-pressed={important}
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-sm px-1.5 text-sm transition-colors sm:h-4',
        FOCUS_STYLES.ringCompact,
        important
          ? 'bg-destructive/15 text-destructive hover:bg-destructive/20 font-medium'
          : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <WarningCircleIcon
        className={ICON_SIZES.compact}
        weight={important ? 'fill' : 'regular'}
      />
      {important ? 'Important' : 'Mark important'}
    </button>
  )
}

/**
 * MetadataRow renders all task/journal metadata in a single responsive row.
 * Task mode: DateField, ImportanceToggle, Topics, People, SuggestedPeople, Project
 * Journal mode: Topics, People, SuggestedPeople, Project
 */
export function MetadataRow({
  effectiveType,
  dueDate,
  onDueDateChange,
  important,
  onImportantChange,
  selectedTopicIds,
  availableTopics,
  onTopicsChange,
  onAddTopic,
  onCreateTopic,
  selectedPeople,
  availablePeople,
  onAddPerson,
  onRemovePerson,
  isPeopleLoading,
  onAddNewPerson,
  suggestedPeople,
  addingPerson,
  onAddSuggestedPerson,
  onDismissSuggestedPerson,
  selectedProject,
  projectOptions,
  onProjectChange,
  projectLocked,
  disabled,
}: MetadataRowProps) {
  const dueDateObj = dueDate ? new Date(dueDate) : undefined

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5 sm:gap-x-2 sm:gap-y-2">
      {/* Due date - task mode only */}
      {effectiveType === 'task' && (
        <DateField
          value={dueDateObj}
          onChange={onDueDateChange}
          disabled={disabled}
          formatDate={(d) => format(d, 'MMM d')}
          placeholder="+ due"
          clearable={!!dueDateObj}
          variant="inline"
        />
      )}

      {/* Importance toggle - task mode only */}
      {effectiveType === 'task' && (
        <ImportanceToggle
          important={important}
          onChange={onImportantChange}
          disabled={disabled}
        />
      )}

      {/* Project - static badge when locked, popover otherwise */}
      {projectLocked && selectedProject ? (
        <span className="text-muted-foreground inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs">
          <PROPERTY_ICONS.project className={ICON_SIZES.compact} />
          {selectedProject.name}
        </span>
      ) : projectOptions.length > 0 ? (
        <InlinePropertyPopover
          value={selectedProject?.id ?? null}
          valueLabel={selectedProject?.name}
          placeholder="Project"
          options={projectOptions}
          onChange={onProjectChange}
          icon={<PROPERTY_ICONS.project className={ICON_SIZES.compact} />}
          allowClear
          disabled={disabled}
        />
      ) : null}

      {/* Topics */}
      <TopicSubtitleSelector
        selectedTopicIds={selectedTopicIds}
        availableTopics={availableTopics}
        onChange={onTopicsChange}
        onAddNew={onAddTopic}
        onCreateTopic={onCreateTopic}
        disabled={disabled}
      />

      {/* People */}
      <MultiSelectProperty
        label="People"
        Icon={PROPERTY_ICONS.people}
        selectedItems={selectedPeople}
        availableItems={availablePeople}
        onAdd={onAddPerson}
        onRemove={onRemovePerson}
        getItemLabel={(p) => p.title}
        disabled={disabled}
        isLoading={isPeopleLoading}
        onAddNew={onAddNewPerson}
        addNewLabel="Add new..."
      />

      {/* Suggested people chips */}
      <SuggestedPeopleChips
        suggestions={suggestedPeople}
        addingPerson={addingPerson}
        isDisabled={disabled}
        onAdd={onAddSuggestedPerson}
        onDismiss={onDismissSuggestedPerson}
      />
    </div>
  )
}
